/**
 * PartCategoryWorkflow Component
 * 
 * Specialized workflow for Dimensions AI QC pipeline with Aceternity animations
 * and Tremor data display patterns. Features read-only prompt configuration
 * optimized for part category specification extraction and validation.
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { globalPromptsAPI } from '../../services/api';
import {
  Grid3x3,
  Home,
  ChevronRight,
  Upload,
  Settings,
  CheckCircle2,
  FileSpreadsheet,
  Loader2,
  AlertCircle,
  X,
  Download,
  FolderOpen,
  Sparkles,
  Zap,
  Lock,
  DollarSign,
  Calculator,
  FileText,
  FileOutput,
  Clock,
  Play,
  AlertTriangle,
  XCircle,
  Database,
} from 'lucide-react';
import { theme } from '../../styles/theme';
import { filesAPI, jobsAPI, authAPI } from '../../services/api';
import type { FileUpload, DigiPARTCatalogue, DigiPARTConnectionStatus } from '../../types';
import { FilePreviewDialog } from '../../components/FilePreviewDialog';
import { CostConfirmation } from '../../components/CostConfirmation';
import { extractApiError } from '../../utils/errorMessages';

interface PipelineConfig {
  pipeline_type: string;
  name: string;
  description: string;
  default_prompt: string;
  default_model: string;
  default_chunk_size: number;
  default_temperature: number;
  default_max_concurrent: number;
  required_columns: string[];
  features: string[];
}

interface CostEstimate {
  estimated_cost: {
    usd: number;
    sgd: number;
  };
  estimated_tokens: {
    total_input_tokens: number;
    total_output_tokens: number;
    grand_total_tokens: number;
  };
  processing_time_minutes?: number;
}

interface ColumnInfo {
  name: string;
  unique_count: number;
  null_count: number;
  uniqueness_ratio: number;
  is_suitable: boolean;
  sample_values: (string | null)[];
}

interface ColumnAnalysisResult {
  file_id: string;
  filename: string;
  total_rows: number;
  total_columns: number;
  columns: ColumnInfo[];
  suggested_join_column: string | null;
  suggestion_reason: string;
}

const PartCategoryWorkflow: React.FC = () => {
  const navigate = useNavigate();
  // Stable per-attempt idempotency key — reset only after a confirmed new job.
  const idempotencyKeyRef = React.useRef<string>(crypto.randomUUID());

  // ─── Mode selector ───────────────────────────────────────────────────────
  type PipelineMode = 'live' | 'csv';
  const [mode, setMode] = useState<PipelineMode>('live');

  // ─── Live digiPART mode state ─────────────────────────────────────────────
  const [liveStep, setLiveStep] = useState<0 | 1>(0);
  const [liveSessionLoading, setLiveSessionLoading] = useState(false);
  const [liveSessionStatus, setLiveSessionStatus] = useState<DigiPARTConnectionStatus | null>(null);
  const [liveSessionError, setLiveSessionError] = useState('');
  const [catalogues, setCatalogues] = useState<DigiPARTCatalogue[]>([]);
  const [loadingCatalogues, setLoadingCatalogues] = useState(false);
  const [catalogueLoadError, setCatalogueLoadError] = useState(false);
  const [selectedCatalogueId, setSelectedCatalogueId] = useState('');
  const [maxParts, setMaxParts] = useState<number | null>(null); // null = no limit
  const [maxPartsError, setMaxPartsError] = useState<string | null>(null);
  const [liveJobName, setLiveJobName] = useState('');
  const [liveStarting, setLiveStarting] = useState(false);
  const [liveStartError, setLiveStartError] = useState('');
  const [liveTemperature, setLiveTemperature] = useState(0.1);
  const [preflightData, setPreflightData] = useState<import('../../types').PartCategoryPreflightData | null>(null);
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [preflightError, setPreflightError] = useState('');
  const [preflightPct, setPreflightPct] = useState(0);
  const preflightDataReadyRef = React.useRef(false);
  const preflightAbortRef = React.useRef(false);

  // Check digiPART session once when mode switches to 'live'
  React.useEffect(() => {
    if (mode === 'live' && !liveSessionStatus) {
      checkLiveSession();
    }
  }, [mode]);

  // Animate preflight through 4 weighted phases (10% / 10% / 40% / 40%).
  // Bar caps at 99% until the backend responds, then advances to 100% and
  // triggers the Scope page by setting preflightLoading = false.
  React.useEffect(() => {
    if (!preflightLoading) {
      setPreflightPct(0);
      return;
    }

    // Compute estimated chunks from the selected catalogue for phases 2+3 counters.
    const selectedCatalogue = catalogues.find(c => c.id === selectedCatalogueId);
    const rawParts: number = (selectedCatalogue as any)?.spare_parts_count ?? 0;
    const limitedParts = maxParts && rawParts > 0 ? Math.min(maxParts, rawParts) : rawParts;
    const estChunks = Math.max(1, Math.ceil(limitedParts > 0 ? limitedParts / 150 : 5));

    // Duration budgets per phase (ms). Phases 2+3 scale with catalogue size.
    const chunkDuration = Math.max(3000, estChunks * 600);
    const PHASE_DURATIONS_MS = [3000, 2000, chunkDuration, chunkDuration];
    // Phase weight ranges: [0–10], [10–20], [20–60], [60–100]
    const PHASE_RANGES = [10, 10, 40, 40];
    const TICK_MS = 100;
    let currentPct = 0;

    const interval = setInterval(() => {
      if (preflightAbortRef.current) {
        clearInterval(interval);
        return;
      }
      const phase = currentPct < 10 ? 0 : currentPct < 20 ? 1 : currentPct < 60 ? 2 : 3;
      const inc = PHASE_RANGES[phase] / (PHASE_DURATIONS_MS[phase] / TICK_MS);
      // Hold at 99% until backend data is ready; the final 1% triggers the transition.
      const cap = preflightDataReadyRef.current ? 100 : 99;
      currentPct = Math.min(cap, currentPct + inc);
      setPreflightPct(Math.round(currentPct));
      if (currentPct >= 100) {
        clearInterval(interval);
        setPreflightLoading(false);
      }
    }, TICK_MS);

    return () => clearInterval(interval);
  }, [preflightLoading]);

  const checkLiveSession = async () => {
    setLiveSessionLoading(true);
    setLiveSessionError('');
    try {
      const response = await authAPI.digipartSessionStatus();
      const status = response.data as any;
      setLiveSessionStatus(status);
      if (status.authenticated) {
        loadCatalogues();
      } else if (!status.assigned) {
        setLiveSessionError('No DigiPART account assigned. Contact your administrator.');
      } else {
        setLiveSessionError('Not connected to DigiPART. Connect via your Profile page first.');
      }
    } catch {
      setLiveSessionError('Failed to check DigiPART connection status.');
    } finally {
      setLiveSessionLoading(false);
    }
  };

  const loadCatalogues = async () => {
    setLoadingCatalogues(true);
    setCatalogueLoadError(false);
    try {
      const response = await jobsAPI.getPartCategoryCatalogues();
      setCatalogues(response.data.catalogues || []);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        // DigiPART session expired — hide the project step and surface the auth banner
        setLiveSessionStatus(null);
        setLiveSessionError('Your DigiPART session has expired. Please reconnect and try again.');
      } else {
        setCatalogueLoadError(true);
      }
    } finally {
      setLoadingCatalogues(false);
    }
  };

  const loadCataloguePreflight = async (catalogueId: string) => {
    if (!catalogueId.trim()) return;
    preflightDataReadyRef.current = false;
    preflightAbortRef.current = false;
    setPreflightPct(0);
    setPreflightLoading(true);
    setPreflightError('');
    setPreflightData(null);
    try {
      const response = await jobsAPI.partCategoryPreflight(catalogueId.trim(), maxParts);
      setPreflightData(response.data);
      // Signal the animation to advance to 100% and then call setPreflightLoading(false).
      preflightDataReadyRef.current = true;
    } catch (err: unknown) {
      // Kill animation immediately on error.
      preflightAbortRef.current = true;
      setPreflightError(extractApiError(err, 'Failed to fetch preflight estimate.'));
      setPreflightLoading(false);
    }
  };

  const handleLiveStart = async () => {
    if (!selectedCatalogueId.trim()) {
      setLiveStartError('Please select or enter a project.');
      return;
    }
    setLiveStarting(true);
    setLiveStartError('');
    try {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const jobName = liveJobName.trim()
        ? `${liveJobName.trim()}-${timestamp}`
        : `PartCategory-Live-${timestamp}`;

      const response = await jobsAPI.start({
        file_id: '',              // Not used for live digiPART flow
        pipeline_type: 'part_category',
        job_name: jobName,
        config: {
          catalogue_id: selectedCatalogueId.trim(),
          temperature: liveTemperature,
          // Pass preflight-derived chunk_size so the Celery task skips re-estimation
          chunk_size: preflightData?.estimated_cost?.chunk_size_planned ?? 0,
          ...(maxParts ? { max_parts: maxParts } : {}),
        },
      }, idempotencyKeyRef.current);
      idempotencyKeyRef.current = crypto.randomUUID();
      navigate(`/jobs/${response.data.id}`);
    } catch (err: unknown) {
      setLiveStartError(extractApiError(err, 'Failed to start job. Check that the live pipeline is enabled on the server.'));
      setLiveStarting(false);
    }
  };

  // ─── CSV mode state ───────────────────────────────────────────────────────
  const [currentStep, setCurrentStep] = useState<0 | 1>(0);
  const [uploadedFiles, setUploadedFiles] = useState<FileUpload[]>([]);
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  
  // File preview state
  const [filePreview, setFilePreview] = useState<string[]>([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string>('');
  const [showPreview, setShowPreview] = useState(false);
  
  // Column analysis state
  const [columnAnalysis, setColumnAnalysis] = useState<ColumnAnalysisResult | null>(null);
  const [analyzingColumns, setAnalyzingColumns] = useState(false);
  const [selectedJoinColumn, setSelectedJoinColumn] = useState<string>('');
  
  // Configuration state
  const [pipelineConfig, setPipelineConfig] = useState<PipelineConfig | null>(null);
  const [chunkSize, setChunkSize] = useState(20);
  const [maxConcurrent, setMaxConcurrent] = useState(5);
  const [temperature, setTemperature] = useState(0.7);
  const [projectName, setProjectName] = useState('');
  const [estimatedCost, setEstimatedCost] = useState<CostEstimate | null>(null);
  const [estimating, setEstimating] = useState(false);
  const [starting, setStarting] = useState(false);
  const [costConfirmed, setCostConfirmed] = useState(false);
  
  // Validation errors
  const [chunkSizeError, setChunkSizeError] = useState<string>('');
  const [maxConcurrentError, setMaxConcurrentError] = useState<string>('');
  
  // Validation helper function
  const validatePositiveInteger = (
    value: string | number,
    min: number,
    max: number,
    fieldName: string
  ): string => {
    const strValue = String(value).trim();
    
    if (!strValue || strValue === '') {
      return `${fieldName} is required`;
    }
    
    if (!/^\d+$/.test(strValue)) {
      return `${fieldName} must be a positive integer`;
    }
    
    const num = Number(strValue);
    
    if (num < min || num > max) {
      return `${fieldName} must be between ${min} and ${max}`;
    }
    
    return '';
  };
  
  // Fetch pipeline configuration on mount
  React.useEffect(() => {
    fetchPipelineConfig();
  }, []);
  
  const fetchPipelineConfig = async () => {
    try {
      const response = await globalPromptsAPI.getByPipelineType('part_category');
      // API returns array, take first result
      const config = Array.isArray(response.data) ? response.data[0] : response.data.results?.[0];
      if (config) {
        setPipelineConfig(config);
        setChunkSize(config.default_chunk_size || 20);
        setMaxConcurrent(config.default_max_concurrent || 5);
        setTemperature(config.default_temperature || 0.7);
      }
    } catch (error) {
      console.error('Failed to fetch pipeline config:', error);
    }
  };
  
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  
  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const files = Array.from(e.dataTransfer.files);
      await handleFileUpload(files);
    }
  };
  
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const files = Array.from(e.target.files);
      await handleFileUpload(files);
    }
  };
  
  const handleFileUpload = async (files: File[]) => {
    setUploading(true);
    
    for (const file of files) {
      try {
        const response = await filesAPI.upload(file);
        setUploadedFiles(prev => [...prev, response.data]);
        
        // Always select and preview the latest uploaded file
        setSelectedFileId(response.data.id);
        // Generate preview for the uploaded file
        await generateFilePreview(file);
        // Analyze columns for join column suggestion
        await analyzeColumns(response.data.id);
      } catch (error) {
        console.error('Upload failed:', error);
      }
    }
    
    setUploading(false);
  };
  
  /**
   * Analyze file columns to suggest best join column
   * Calls backend API to get uniqueness ratios for each column
   */
  const analyzeColumns = async (fileId: string) => {
    setAnalyzingColumns(true);
    
    try {
      const response = await jobsAPI.analyzeColumns({ file_id: fileId });
      setColumnAnalysis(response.data);
      
      // Auto-select suggested join column if available
      if (response.data.suggested_join_column) {
        setSelectedJoinColumn(response.data.suggested_join_column);
      }
    } catch (error) {
      console.error('Column analysis failed:', error);
      setColumnAnalysis(null);
    } finally {
      setAnalyzingColumns(false);
    }
  };
  
  /**
   * Generate a preview of the file content (first 100 lines)
   * Supports both CSV and Excel files
   */
  const generateFilePreview = async (file: File) => {
    setLoadingPreview(true);
    setPreviewError('');
    
    try {
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      
      if (fileExtension === 'csv') {
        // Handle CSV files with FileReader
        const text = await file.text();
        const lines = text.split('\n').slice(0, 100);
        setFilePreview(lines);
      } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
        // Handle Excel files with SheetJS (xlsx)
        const XLSX = await import('xlsx');
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        
        // Read first sheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Convert to CSV format for preview
        const csv = XLSX.utils.sheet_to_csv(worksheet);
        const lines = csv.split('\n').slice(0, 100);
        setFilePreview(lines);
      } else {
        setPreviewError('Unsupported file format. Please upload a CSV or Excel file.');
      }
    } catch (error) {
      console.error('Failed to generate preview:', error);
      setPreviewError('Failed to read file content. Please ensure the file is not corrupted.');
    } finally {
      setLoadingPreview(false);
    }
  };
  
  const removeFile = (fileId: string) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
    if (selectedFileId === fileId) {
      setSelectedFileId(uploadedFiles[0]?.id || null);
    }
  };
  
  const handleEstimateCost = async () => {
    if (!selectedFileId || !pipelineConfig) return;
    
    setEstimating(true);
    try {
      const response = await jobsAPI.estimate({
        file_id: selectedFileId,
        pipeline_type: 'part_category',
        prompt_text: pipelineConfig.default_prompt,
        model_name: pipelineConfig.default_model,
        chunk_size: chunkSize,
        max_concurrent: maxConcurrent,
        temperature: temperature,
        output_multiplier: 1.8,  // Default multiplier for estimating output tokens
      });
      setEstimatedCost(response.data);
    } catch (error) {
      console.error('Cost estimation failed:', error);
    } finally {
      setEstimating(false);
    }
  };
  
  const handleStartProcessing = async () => {
    if (!selectedFileId || !pipelineConfig) return;
    
    setStarting(true);
    try {
      // Generate unique job name with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const jobName = projectName ? `${projectName}-${timestamp}` : `PartCategory-${timestamp}`;
      
      const response = await jobsAPI.start({
        file_id: selectedFileId,
        pipeline_type: 'part_category',
        project_name: projectName || '',
        job_name: jobName,
        prompt_text: pipelineConfig.default_prompt,
        chunk_size: chunkSize,
        max_concurrent: maxConcurrent,
        temperature: temperature,
        join_column: selectedJoinColumn || undefined,  // Pass join column to backend
      }, idempotencyKeyRef.current);
      idempotencyKeyRef.current = crypto.randomUUID();
      
      // Navigate to job detail page
      navigate(`/jobs/${response.data.id}`);
    } catch (error) {
      console.error('Failed to start job:', error);
      setStarting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-bg-base">
      {/* Breadcrumb */}
      <nav className="flex items-center text-sm text-text-tertiary mb-6" aria-label="Breadcrumb">
        <Link to="/dashboard" className="flex items-center hover:text-text-primary transition-colors">
          <Home className="w-4 h-4" />
        </Link>
        <ChevronRight className="w-4 h-4 mx-2" />
        <Link to="/select-pipeline" className="hover:text-text-primary transition-colors">
          Select Pipeline
        </Link>
        <ChevronRight className="w-4 h-4 mx-2" />
        <span className="text-text-primary font-medium" aria-current="page">Part Category QC</span>
      </nav>

      {/* Mode Selector */}
      <div className="bg-bg-surface border border-border rounded-xl p-1.5 inline-flex gap-1 mb-8">
        <button
          onClick={() => setMode('live')}
          className={`px-5 py-2.5 rounded-lg font-medium transition-all duration-200 flex items-center gap-2 text-sm ${
            mode === 'live'
              ? 'bg-interactive-primary text-text-inverse shadow'
              : 'text-text-secondary hover:text-text-primary hover:bg-bg-base/50'
          }`}
          data-testid="mode-live-tab"
        >
          <Database className="w-4 h-4" />
          Live digiPART
          {/* A hairline, not a translucent wash: `bg-bg-surface/20` lightened
              the accent fill underneath just enough to pull this label from
              6.76:1 down to 4.45:1. The border marks it as a badge without
              touching the background it sits on. */}
          <span className="ml-1 rounded-full border border-current px-1.5 py-0.5 text-xs">New</span>
        </button>
        <button
          onClick={() => setMode('csv')}
          className={`px-5 py-2.5 rounded-lg font-medium transition-all duration-200 flex items-center gap-2 text-sm ${
            mode === 'csv'
              ? 'bg-bg-surface text-primary shadow'
              : 'text-text-secondary hover:text-text-primary hover:bg-bg-base/50'
          }`}
          data-testid="mode-csv-tab"
        >
          <Upload className="w-4 h-4" />
          CSV File Upload
        </button>
      </div>

      {/* ─── Live digiPART Mode ─────────────────────────────────────────────── */}
      {mode === 'live' && (
        <div className="space-y-6" data-testid="live-mode-section">

          {/* ── Step indicator ─────────────────────────────────────────────── */}
          <div className="flex items-center justify-center gap-0 mb-2">
            {(['Connect', 'Configure'] as const).map((label, idx) => (
              <React.Fragment key={label}>
                <div className="flex flex-col items-center">
                  <div
                    className={[
                      'w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all',
                      liveStep > idx
                        ? 'bg-qc-success text-text-inverse'
                        : liveStep === idx
                        ? 'bg-interactive-primary text-text-inverse ring-4 ring-border-strong'
                        : 'bg-bg-surface border-2 border-border text-text-tertiary',
                    ].join(' ')}
                    data-testid={`live-step-${idx}`}
                  >
                    {liveStep > idx ? <CheckCircle2 className="w-4 h-4" /> : idx + 1}
                  </div>
                  <span className={`text-xs mt-1 font-medium ${liveStep === idx ? theme.text.primary : theme.text.tertiary}`}>
                    {label}
                  </span>
                </div>
                {idx === 0 && (
                  <div className="w-12 h-0.5 bg-border-default mx-3 mb-4" />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Connection Check Card */}
          <div className={`${theme.card.background} rounded-lg shadow p-6`}>
            <h2 className={`text-2xl font-bold ${theme.text.primary} mb-4 flex items-center`}>
              <Database className="w-6 h-6 mr-2" />
              DigiPART Connection Check
            </h2>

            {liveSessionLoading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-text-secondary" />
                <span className={`ml-3 ${theme.text.secondary}`}>Checking connection...</span>
              </div>
            )}

            {!liveSessionLoading && liveSessionStatus?.authenticated && (
              <div className="space-y-4">
                <div className="p-4 bg-qc-success/10 rounded-lg border border-qc-success/30">
                  <div className="flex items-center">
                    <CheckCircle2 className="w-6 h-6 text-qc-success mr-3" />
                    <div className="flex-1">
                      <p className="font-semibold text-qc-success">
                        ✅ Connected to DigiPART
                      </p>
                      <p className={`text-sm ${theme.text.secondary} mt-1`}>
                        Logged in as: <strong>{liveSessionStatus.user_email}</strong>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!liveSessionLoading && liveSessionError && (
              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-qc-warning/10 border border-qc-warning/30">
                  <div className="flex items-start">
                    <AlertTriangle className="w-6 h-6 text-qc-warning mr-3 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-qc-warning">
                        Action Required
                      </p>
                      <p className={`text-sm ${theme.text.secondary} mt-1`}>
                        {liveSessionError}
                      </p>
                      <div className="flex gap-2 mt-3">
                        <Link
                          to="/profile?tab=digipart"
                          className="inline-block px-4 py-2 bg-qc-warning hover:bg-qc-warning/90 text-text-inverse rounded-lg transition text-sm"
                        >
                          Login to DigiPART
                        </Link>
                        <button
                          onClick={checkLiveSession}
                          className={`px-4 py-2 border border-border rounded-lg text-sm ${theme.text.secondary} hover:bg-bg-base transition`}
                        >
                          Retry
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── STEP 0 — Catalogue selection ────────────────────────────────── */}
          {liveSessionStatus?.authenticated && liveStep === 0 && (
            <div className={`${theme.card.background} rounded-lg shadow p-6`} data-testid="live-step0-card">
              <h2 className={`text-2xl font-bold ${theme.text.primary} mb-6 flex items-center`}>
                <Settings className="w-6 h-6 mr-2" />
                Select Project
              </h2>

              {/* Project selector */}
              <div className="mb-4">
                <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                  DigiPART Project <span className="text-text-tertiary" aria-hidden="true">*</span>
                </label>
                {loadingCatalogues ? (
                  <div className="flex items-center py-3">
                    <Loader2 className="w-5 h-5 animate-spin text-text-secondary" />
                    <span className={`ml-2 text-sm ${theme.text.secondary}`}>Loading projects...</span>
                  </div>
                ) : catalogues.length > 0 ? (
                  <select
                    value={selectedCatalogueId}
                    onChange={(e) => {
                      setSelectedCatalogueId(e.target.value);
                      setPreflightData(null);
                      if (e.target.value) loadCataloguePreflight(e.target.value);
                    }}
                    data-testid="catalogue-select"
                    className={`w-full px-4 py-2 border ${
                      selectedCatalogueId
                        ? 'border-qc-success'
                        : 'border-border'
                    } rounded-lg bg-bg-surface ${theme.text.primary} focus:ring-2 focus:ring-border-strong focus:border-transparent`}
                  >
                    <option value="">— Select a Project —</option>
                    {catalogues.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                        {(cat as any).spare_parts_count != null
                          ? ` (${(cat as any).spare_parts_count} parts)`
                          : ''}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div
                    data-testid="catalogue-load-error"
                    className="flex items-center justify-between px-4 py-3 border border-qc-error/30 rounded-lg bg-qc-error/10"
                  >
                    <span className="text-sm text-qc-error">
                      {catalogueLoadError
                        ? 'Could not load projects from DigiPART. Check your connection and try again.'
                        : 'No projects found in DigiPART.'}
                    </span>
                    <button
                      type="button"
                      onClick={loadCatalogues}
                      className="ml-4 text-sm font-medium text-text-secondary hover:underline"
                    >
                      Retry
                    </button>
                  </div>
                )}
                <p className={`text-xs ${theme.text.secondary} mt-1`}>
                  The pipeline will fetch all uncategorised parts in this project directly from DigiPART.
                </p>
              </div>

              {/* Test limit filter */}
              <div className="mb-6">
                <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                  Part limit <span className={`font-normal ${theme.text.tertiary}`}>(optional)</span>
                </label>
                <input
                  type="number"
                  value={maxParts ?? ''}
                  onChange={(e) => {
                    if (e.target.value === '') {
                      setMaxPartsError(null);
                      setMaxParts(null);
                      setPreflightData(null);
                      return;
                    }
                    const num = e.target.valueAsNumber;
                    if (Number.isInteger(num) && num >= 1) {
                      setMaxPartsError(null);
                      setMaxParts(Math.min(num, 100_000));
                      setPreflightData(null);
                    } else {
                      setMaxPartsError('Enter a whole number of 1 or more.');
                    }
                  }}
                  placeholder="Unlimited"
                  min="1"
                  max="100000"
                  step="1"
                  data-testid="max-parts-input"
                  aria-invalid={maxPartsError != null}
                  aria-describedby="max-parts-hint"
                  className={`w-full px-4 py-2 border rounded-lg bg-bg-surface ${theme.text.primary} focus:ring-2 focus:border-transparent ${
                    maxPartsError
                      ? 'border-qc-error focus:ring-qc-error'
                      : 'border-border focus:ring-border-strong'
                  }`}
                />
                {maxPartsError
                  ? <p id="max-parts-hint" className="text-xs text-qc-error mt-1" role="alert">{maxPartsError}</p>
                  : <p id="max-parts-hint" className={`text-xs ${theme.text.tertiary} mt-1`}>Leave empty to process all uncategorised parts. Put a number to filter.</p>
                }
              </div>

              {/* Preflight — 4-phase weighted progress bar (10/10/40/40).
                   Holds at 99% until backend data arrives, then snaps to 100%
                   and transitions to the Scope card. */}
              {preflightLoading && (() => {
                const PREFLIGHT_PHASES = [
                  { label: 'Fetching from DigiPART',    pctEnd: 10,  weight: 10 },
                  { label: 'Building initial chunk size', pctEnd: 20, weight: 10 },
                  { label: 'Chunking parts',             pctEnd: 60,  weight: 40 },
                  { label: 'Validating tokens',          pctEnd: 100, weight: 40 },
                ];
                const currentPhaseIdx =
                  preflightPct < 10 ? 0 :
                  preflightPct < 20 ? 1 :
                  preflightPct < 60 ? 2 : 3;

                // Chunk counter for phases 2+3 (Chunking and Validating).
                const selectedCatalogue = catalogues.find(c => c.id === selectedCatalogueId);
                const rawParts: number = (selectedCatalogue as any)?.spare_parts_count ?? 0;
                const limitedParts = maxParts && rawParts > 0 ? Math.min(maxParts, rawParts) : rawParts;
                const estChunks = Math.max(1, Math.ceil(limitedParts > 0 ? limitedParts / 150 : 5));

                let displayLabel: string;
                switch (currentPhaseIdx) {
                  case 0:
                    displayLabel = 'Fetching from DigiPART…';
                    break;
                  case 1:
                    displayLabel = 'Building initial chunk size…';
                    break;
                  case 2: {
                    const chunkNum = Math.max(1, Math.ceil(Math.max(0, (preflightPct - 20) / 40) * estChunks));
                    displayLabel = `Chunking parts… (${chunkNum} / ~${estChunks})`;
                    break;
                  }
                  default: {
                    const chunkNum = Math.max(1, Math.ceil(Math.max(0, (preflightPct - 60) / 40) * estChunks));
                    displayLabel = `Validating tokens… (${chunkNum} / ~${estChunks})`;
                  }
                }

                return (
                  <div className="py-4 space-y-3" data-testid="preflight-progress-bar">
                    {/* Label + big percentage */}
                    <div className="flex items-end justify-between gap-2">
                      <span className="text-sm font-medium text-text-secondary">{displayLabel}</span>
                      <span className="text-2xl font-bold tabular-nums text-text-secondary leading-none shrink-0">
                        {preflightPct}%
                      </span>
                    </div>

                    {/* Main progress track */}
                    <div
                      className="w-full h-2.5 bg-bg-hover rounded-full overflow-hidden"
                      role="progressbar"
                      aria-valuenow={preflightPct}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-label={displayLabel}
                    >
                      <div
                        className="h-full bg-bg-subtle rounded-full transition-all duration-100"
                        style={{ width: `${preflightPct}%` }}
                      />
                    </div>

                    {/* Phase segment pills — widths proportional to phase weights (10/10/40/40).
                         Title attribute shows the phase name on hover. */}
                    <div className="flex gap-1">
                      {PREFLIGHT_PHASES.map((p, i) => (
                        <div
                          key={i}
                          style={{ width: `${p.weight}%` }}
                          title={`Phase ${i + 1}: ${p.label}`}
                          className={`h-1 rounded-full transition-colors duration-300 ${
                            i < currentPhaseIdx
                              ? 'bg-interactive-primary'
                              : i === currentPhaseIdx
                              ? 'bg-interactive-primary/70'
                              : 'bg-bg-hover'
                          }`}
                        />
                      ))}
                    </div>

                    <p className="text-xs text-text-tertiary text-right">
                      Phase {currentPhaseIdx + 1} of {PREFLIGHT_PHASES.length}
                    </p>
                  </div>
                );
              })()}

              {/* Preflight error */}
              {preflightError && (
                <div className="mb-4 p-3 bg-qc-warning/10 border border-qc-warning/30 rounded-lg flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-qc-warning mt-0.5 shrink-0" />
                  <p className="text-sm text-qc-warning">{preflightError}</p>
                </div>
              )}

              {/* Catalogue preview card */}
              {preflightData && !preflightLoading && (
                <div className="mb-6 p-4 rounded-lg bg-bg-base border border-border" data-testid="catalogue-preview-card">
                  <h3 className={`text-sm font-semibold ${theme.text.primary} mb-3 flex items-center gap-2`}>
                    <Calculator className="w-4 h-4" />
                    Catalogue Scope
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className={`text-xs ${theme.text.tertiary}`}>Total Parts</p>
                      <p className={`text-xl font-bold ${theme.text.primary}`}>
                        {preflightData.total_parts.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className={`text-xs ${theme.text.tertiary}`}>Will Process</p>
                      <p className="text-xl font-bold text-qc-success">
                        {preflightData.uncategorised_parts.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className={`text-xs ${theme.text.tertiary}`}>Skip (tagged)</p>
                      <p className={`text-xl font-bold ${theme.text.secondary}`}>
                        {(preflightData.total_parts - preflightData.uncategorised_parts).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  {preflightData.uncategorised_parts === 0 && (
                    <div className="mt-3 flex items-center gap-2 text-sm text-qc-warning">
                      <AlertTriangle className="w-4 h-4" />
                      All parts are already categorised — nothing to process.
                    </div>
                  )}

                  {/* Chunk discovery summary */}
                  {preflightData.estimated_cost?.chunk_discovery && (
                    <div className="mt-4 pt-4 border-t border-border" data-testid="chunk-discovery-summary">
                      <p className={`text-xs font-semibold ${theme.text.tertiary} mb-2 uppercase tracking-wide`}>
                        Chunk Discovery
                      </p>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className={theme.text.tertiary}>Optimal chunk size</span>
                          <span className={`font-semibold ${theme.text.primary}`}>
                            {preflightData.estimated_cost.chunk_discovery.confirmed_chunk_size} parts
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className={theme.text.tertiary}>Total chunks</span>
                          <span className={`font-semibold ${theme.text.primary}`}>
                            {preflightData.estimated_cost.total_chunks ?? '—'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className={theme.text.tertiary}>Iterations to converge</span>
                          <span className={`font-semibold ${theme.text.primary}`}>
                            {preflightData.estimated_cost.chunk_discovery.reductions_needed + 1}
                          </span>
                        </div>
                        {preflightData.estimated_cost.chunk_discovery.reductions_needed > 0 && (
                          <div className="flex justify-between col-span-2">
                            <span className={`text-xs ${theme.text.tertiary}`}>
                              Initial estimate was {preflightData.estimated_cost.chunk_discovery.initial_estimate} parts —
                              reduced {preflightData.estimated_cost.chunk_discovery.reductions_needed} time(s)
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Next button */}
              <div className="flex justify-end">
                <button
                  onClick={() => setLiveStep(1)}
                  disabled={
                    !selectedCatalogueId.trim() ||
                    (preflightData !== null && preflightData.uncategorised_parts === 0)
                  }
                  data-testid="live-next-btn"
                  className="px-6 py-2.5 bg-interactive-primary disabled:opacity-50 disabled:cursor-not-allowed text-text-inverse font-semibold rounded-lg transition flex items-center gap-2"
                >
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 1 — Configure & Start ──────────────────────────────────── */}
          {liveSessionStatus?.authenticated && liveStep === 1 && (
            <div className={`${theme.card.background} rounded-lg shadow p-6`} data-testid="live-step1-card">
              <h2 className={`text-2xl font-bold ${theme.text.primary} mb-6 flex items-center`}>
                <Settings className="w-6 h-6 mr-2" />
                Configure Job
              </h2>

              {/* Job name */}
              <div className="mb-6">
                <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                  Job Name <span className={`font-normal ${theme.text.tertiary}`}>(optional)</span>
                </label>
                <input
                  type="text"
                  value={liveJobName}
                  onChange={(e) => setLiveJobName(e.target.value)}
                  placeholder="e.g. ACME Catalogue — May 2026"
                  data-testid="live-job-name-input"
                  className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary} focus:ring-2 focus:ring-border-strong`}
                />
              </div>

              {/* Model (read-only) */}
              {pipelineConfig?.default_model && (
                <div className="mb-6">
                  <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                    Model
                  </label>
                  <div className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg bg-bg-base">
                    <Lock className="w-4 h-4 text-text-tertiary" />
                    <span className={`text-sm font-mono ${theme.text.primary}`}>{pipelineConfig.default_model}</span>
                    <span className={`ml-auto text-xs ${theme.text.tertiary}`}>set by admin</span>
                  </div>
                </div>
              )}

              {/* Temperature slider */}
              <div className="mb-6">
                <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                  Temperature: <span className="font-bold text-text-secondary">{liveTemperature.toFixed(2)}</span>
                </label>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.01}
                  value={liveTemperature}
                  onChange={(e) => setLiveTemperature(parseFloat(e.target.value))}
                  data-testid="live-temperature-slider"
                  className="w-full"
                />
                <div className={`flex justify-between text-xs ${theme.text.tertiary} mt-1`}>
                  <span>0 — deterministic</span>
                  <span>1 — creative</span>
                </div>
              </div>

              {/* Cost estimate panel */}
              {preflightData?.estimated_cost?.total_chunks != null && (
                <div
                  className="mb-6 p-4 rounded-lg bg-bg-base border border-border"
                  data-testid="cost-estimate-panel"
                >
                  <h3 className={`text-sm font-semibold ${theme.text.primary} mb-3 flex items-center gap-2`}>
                    <DollarSign className="w-4 h-4" />
                    Estimated Cost
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
                    <div>
                      <p className={`text-xs ${theme.text.tertiary}`}>Chunks</p>
                      <p className={`text-lg font-bold ${theme.text.primary}`}>
                        {preflightData.estimated_cost!.total_chunks}
                      </p>
                    </div>
                    <div>
                      <p className={`text-xs ${theme.text.tertiary}`}>Input tokens</p>
                      <p className={`text-lg font-bold ${theme.text.primary}`}>
                        {(preflightData.estimated_cost!.total_input_tokens ?? 0).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className={`text-xs ${theme.text.tertiary}`}>Est. USD</p>
                      <p className="text-lg font-bold text-qc-success">
                        ${(preflightData.estimated_cost!.usd ?? 0).toFixed(4)}
                      </p>
                    </div>
                    <div>
                      <p className={`text-xs ${theme.text.tertiary}`}>Est. SGD</p>
                      <p className="text-lg font-bold text-qc-success">
                        S${(preflightData.estimated_cost!.sgd ?? 0).toFixed(4)}
                      </p>
                    </div>
                  </div>
                  <p className={`text-xs ${theme.text.tertiary} mt-2`}>
                    {preflightData.estimated_cost!.pricing_note}
                  </p>
                </div>
              )}

              {liveStartError && (
                <div className="mb-4 p-4 bg-qc-error/10 rounded-lg border border-qc-error/30 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-qc-error mt-0.5 shrink-0" />
                  <p className="text-sm text-qc-error">{liveStartError}</p>
                </div>
              )}

              <div className="flex items-center justify-between">
                <button
                  onClick={() => { setLiveStep(0); setLiveStartError(''); }}
                  data-testid="live-back-btn"
                  className={`px-4 py-2 border border-border rounded-lg text-sm ${theme.text.secondary} hover:bg-bg-base transition flex items-center gap-2`}
                >
                  ← Back
                </button>
                <button
                  onClick={handleLiveStart}
                  disabled={liveStarting}
                  data-testid="live-start-btn"
                  className="px-6 py-3 bg-interactive-primary hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed text-text-inverse font-semibold rounded-lg transition flex items-center gap-2"
                >
                  {liveStarting ? (
                    <><Loader2 className="w-5 h-5 animate-spin" />Starting...</>
                  ) : (
                    <><Play className="w-5 h-5" />Start Live Pipeline</>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── CSV File Upload Mode ────────────────────────────────────────────── */}
      {mode === 'csv' && (
      <div>
      <div className="flex items-center justify-between mb-8" role="progressbar" aria-valuenow={currentStep} aria-valuemin={0} aria-valuemax={1}>
        {/* Step 1: Upload Files */}
        <div className="flex items-center flex-1">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
              currentStep >= 0
                ? 'bg-interactive-primary text-text-inverse shadow-lg'
                : 'bg-bg-surface border-2 border-border text-text-tertiary'
            }`}
            data-testid="workflow-step-0"
          >
            {currentStep > 0 ? <CheckCircle2 className="w-5 h-5" /> : '1'}
          </div>
          <span className={`ml-3 font-medium ${currentStep >= 0 ? theme.text.primary : theme.text.tertiary}`}>
            Upload Files
          </span>
        </div>
        
        {/* Connector */}
        <div className="flex-1 h-0.5 bg-border-default mx-4"></div>
        
        {/* Step 2: Configure & Process */}
        <div className="flex items-center flex-1 justify-end">
          <span className={`mr-3 font-medium ${currentStep >= 1 ? theme.text.primary : theme.text.tertiary}`}>
            Configure & Process
          </span>
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
              currentStep >= 1
                ? 'bg-interactive-primary text-text-inverse shadow-lg'
                : 'bg-bg-surface border-2 border-border text-text-tertiary'
            }`}
            data-testid="workflow-step-1"
          >
            2
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-bg-surface border border-border rounded-lg p-1.5 inline-flex gap-1 mb-8">
        <button
          onClick={() => setCurrentStep(0)}
          className={`px-6 py-3 rounded-md font-medium transition-all duration-300 flex items-center gap-2 ${
            currentStep === 0
              ? 'bg-bg-surface text-primary shadow-sm'
              : 'text-text-secondary hover:text-text-primary hover:bg-bg-base/50'
          }`}
          data-testid="upload-tab"
        >
          <Upload className="w-4 h-4" />
          Upload
        </button>
        <button
          onClick={() => setCurrentStep(1)}
          disabled={!selectedFileId}
          className={`px-6 py-3 rounded-md font-medium transition-all duration-300 flex items-center gap-2 ${
            currentStep === 1
              ? 'bg-bg-surface text-primary shadow-sm'
              : 'text-text-secondary hover:text-text-primary hover:bg-bg-base/50 disabled:opacity-50 disabled:cursor-not-allowed'
          }`}
          data-testid="configure-tab"
        >
          <Settings className="w-4 h-4" />
          Configure
        </button>
      </div>

      {/* Tab Content */}
      <div className="pt-8 min-h-[500px]">
        {currentStep === 0 && (
          <div data-testid="upload-zone">
            {/* Upload Zone */}
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`relative group border-2 border-dashed rounded-2xl bg-bg-surface/50 backdrop-blur-sm p-12 text-center transition-all duration-300 ${
                dragActive
                  ? 'border-primary bg-primary/5 scale-[1.02]'
                  : 'border-border hover:border-primary hover:bg-primary/5'
              }`}
            >
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-bg-subtle flex items-center justify-center">
                <Upload className="w-16 h-16 text-text-tertiary" />
              </div>
              <h3 className={`text-xl font-semibold ${theme.text.primary} mb-2`}>
                Drop your files here
              </h3>
              <p className={`text-sm ${theme.text.secondary} mb-4`}>
                or click to browse
              </p>
              <div className="flex gap-2 justify-center mb-4">
                <span className="px-3 py-1 bg-bg-surface border border-border rounded-full text-xs">
                  .xlsx
                </span>
                <span className="px-3 py-1 bg-bg-surface border border-border rounded-full text-xs">
                  .xls
                </span>
                <span className="px-3 py-1 bg-bg-surface border border-border rounded-full text-xs">
                  .csv
                </span>
              </div>
              <label className="inline-flex items-center gap-2 text-primary hover:underline cursor-pointer">
                <FolderOpen className="w-4 h-4" />
                Browse files
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileChange}
                  multiple
                  className="hidden"
                />
              </label>
            </div>

            {/* Download Template Button */}
            <div className="mt-6 text-center">
              <button
                className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded-lg hover:bg-bg-surface transition-colors"
                onClick={() => window.open('/api/v1/data-utilities/pipeline-template/?type=part_category', '_blank')}
              >
                <Download className="w-4 h-4" />
                Download Example Template for Part Category QC
              </button>
            </div>

            {/* File Preview Cards */}
            {uploadedFiles.length > 0 && (
              <div className="grid gap-4 mt-6">
                {uploadedFiles.map((file) => (
                  <div
                    key={file.id}
                    className={`bg-bg-surface/80 backdrop-blur border rounded-xl p-4 flex items-center justify-between transition-all duration-200 ${
                      selectedFileId === file.id
                        ? 'border-primary shadow-lg'
                        : 'border-border hover:border-primary/50'
                    }`}
                    onClick={() => setSelectedFileId(file.id)}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="flex items-center gap-4">
                      <FileSpreadsheet className="w-10 h-10 text-primary" />
                      <div>
                        <p className={`font-medium ${theme.text.primary} truncate max-w-md`}>
                          {file.original_filename}
                        </p>
                        <p className={`text-sm ${theme.text.secondary}`}>
                          {(file.file_size / 1024).toFixed(2)} KB • {file.row_count} rows
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="px-3 py-1 bg-qc-success/10 text-qc-success rounded-full text-xs flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        Complete
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(file.id);
                        }}
                        className="p-2 hover:bg-qc-error/20 rounded-full transition-colors"
                        aria-label="Remove file"
                      >
                        <X className="w-4 h-4 text-qc-error" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* File Content Preview - Button */}
            {uploadedFiles.length > 0 && filePreview.length > 0 && (
              <div className="mt-6">
                <button
                  onClick={() => setShowPreview(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-bg-surface hover:bg-bg-base border border-border rounded-lg transition-colors"
                >
                  <FileText className="w-4 h-4 text-primary" />
                  <span className={`text-sm font-medium ${theme.text.primary}`}>
                    Show File Preview
                  </span>
                  <span className={`text-xs ${theme.text.secondary}`}>
                    ({filePreview.length} rows)
                  </span>
                </button>
              </div>
            )}

            {/* File Preview Dialog (Modal) */}
            <FilePreviewDialog
              open={showPreview}
              filePreview={filePreview}
              onClose={() => setShowPreview(false)}
            />

            {/* Column Analysis Section */}
            {uploadedFiles.length > 0 && columnAnalysis && (
              <div className="mt-6 bg-bg-surface border border-border rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-primary" />
                    <h3 className={`text-lg font-semibold ${theme.text.primary}`}>
                      Join Column Detection
                    </h3>
                  </div>
                  {analyzingColumns && (
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-primary" />
                      <span className={`text-sm ${theme.text.secondary}`}>
                        Analyzing...
                      </span>
                    </div>
                  )}
                </div>

                {columnAnalysis.suggested_join_column && (
                  <div className="mb-4 p-4 bg-qc-success/10 border border-qc-success/30 rounded-lg">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-qc-success flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-qc-success mb-1">
                          Recommended Join Column
                        </p>
                        <p className="text-sm text-qc-success">
                          {columnAnalysis.suggestion_reason}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mb-4">
                  <label className={`block text-sm font-medium ${theme.text.primary} mb-2`}>
                    Select Join Column
                    <span className="ml-1 text-text-tertiary" aria-hidden="true">*</span>
                  </label>
                  <select
                    value={selectedJoinColumn}
                    onChange={(e) => setSelectedJoinColumn(e.target.value)}
                    className={`w-full px-4 py-2 bg-bg-base border border-border rounded-lg ${theme.text.primary} focus:outline-none focus:ring-2 focus:ring-primary`}
                  >
                    <option value="">-- Select a column --</option>
                    {columnAnalysis.columns
                      .sort((a, b) => b.uniqueness_ratio - a.uniqueness_ratio)
                      .map((col) => (
                        <option key={col.name} value={col.name}>
                          {col.name} ({(col.uniqueness_ratio * 100).toFixed(1)}% unique
                          {col.is_suitable ? ' ✓' : ''})
                        </option>
                      ))}
                  </select>
                  <p className={`mt-1 text-xs ${theme.text.secondary}`}>
                    💡 Choose a column with high uniqueness (ideally 100%) and no null values
                  </p>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className={`text-left py-2 px-3 ${theme.text.secondary} font-medium`}>Column</th>
                        <th className={`text-right py-2 px-3 ${theme.text.secondary} font-medium`}>Unique</th>
                        <th className={`text-right py-2 px-3 ${theme.text.secondary} font-medium`}>Nulls</th>
                        <th className={`text-right py-2 px-3 ${theme.text.secondary} font-medium`}>Uniqueness</th>
                        <th className={`text-left py-2 px-3 ${theme.text.secondary} font-medium`}>Sample Values</th>
                      </tr>
                    </thead>
                    <tbody>
                      {columnAnalysis.columns
                        .sort((a, b) => b.uniqueness_ratio - a.uniqueness_ratio)
                        .map((col) => (
                          <tr
                            key={col.name}
                            className={`border-b border-border/50 hover:bg-bg-base transition-colors ${
                              col.name === selectedJoinColumn ? 'bg-primary/10' : ''
                            }`}
                          >
                            <td className={`py-2 px-3 font-medium ${theme.text.primary}`}>
                              {col.name}
                              {col.is_suitable && (
                                <span className="ml-2 text-qc-success">✓</span>
                              )}
                            </td>
                            <td className={`py-2 px-3 text-right ${theme.text.primary}`}>
                              {col.unique_count.toLocaleString()}
                            </td>
                            <td className={`py-2 px-3 text-right ${col.null_count > 0 ? 'text-qc-error' : theme.text.secondary}`}>
                              {col.null_count.toLocaleString()}
                            </td>
                            <td className={`py-2 px-3 text-right ${theme.text.primary}`}>
                              <span className={`px-2 py-1 rounded ${
                                col.uniqueness_ratio >= 0.95
                                  ? 'bg-qc-success/10 text-qc-success'
                                  : col.uniqueness_ratio >= 0.8
                                  ? 'bg-qc-warning/10 text-qc-warning'
                                  : 'bg-qc-error/10 text-qc-error'
                              }`}>
                                {(col.uniqueness_ratio * 100).toFixed(1)}%
                              </span>
                            </td>
                            <td className={`py-2 px-3 text-xs ${theme.text.secondary} font-mono`}>
                              {col.sample_values.slice(0, 3).join(', ')}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                <div className={`mt-4 text-xs ${theme.text.tertiary}`}>
                  📊 Showing {columnAnalysis.columns.length} columns from {columnAnalysis.total_rows.toLocaleString()} rows
                </div>
              </div>
            )}

            {/* Continue Button */}
            {uploadedFiles.length > 0 && (
              <div className="mt-8 flex justify-end">
                <button
                  onClick={() => setCurrentStep(1)}
                  className="px-6 py-3 bg-interactive-primary text-text-inverse rounded-lg font-semibold hover:shadow-lg transition-all duration-300 flex items-center gap-2"
                >
                  Continue to Configuration
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}

        {currentStep === 1 && pipelineConfig && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-testid="config-section">
            {/* Left Column: Configuration */}
            <div className="lg:col-span-2 space-y-6">
              {/* AI Prompt Section */}
              <div className="bg-bg-surface border border-border rounded-xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-5 h-5 text-primary" />
                  <h3 className={`text-lg font-semibold ${theme.text.primary}`}>
                    AI Prompt Template
                  </h3>
                </div>
                <div className="mb-2 flex items-center gap-2">
                  <Lock className="w-4 h-4 text-text-tertiary" />
                  <span className={`text-sm ${theme.text.secondary}`}>
                    Auto-configured for Part Category QC
                  </span>
                  <span className="px-2 py-0.5 bg-interactive-primary/10 text-interactive-primary text-xs rounded-full">
                    Optimized
                  </span>
                </div>
                <textarea
                  value={pipelineConfig.default_prompt}
                  readOnly
                  className="w-full h-32 bg-bg-base border border-border rounded-lg p-4 font-data text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <div className="flex justify-between items-center mt-2">
                  <p className={`text-xs ${theme.text.tertiary}`}>
                    {pipelineConfig?.default_prompt?.length || 0} characters
                  </p>
                  <p className={`text-xs ${theme.text.tertiary}`}>
                    💡 Use Generic Pipeline for custom prompts
                  </p>
                </div>
              </div>

              {/* Model Settings */}
              <div className="bg-bg-surface border border-border rounded-xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Zap className="w-5 h-5 text-primary" />
                  <h3 className={`text-lg font-semibold ${theme.text.primary}`}>
                    Model Settings
                  </h3>
                </div>
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-text-tertiary" />
                  <span className={`px-3 py-1 bg-bg-base border border-border rounded-lg text-sm ${theme.text.primary}`}>
                    {pipelineConfig.default_model}
                  </span>
                  <span className={`text-sm ${theme.text.tertiary}`}>
                    (Locked for consistency)
                  </span>
                </div>
              </div>

              {/* Processing Parameters */}
              <div className="bg-bg-surface border border-border rounded-xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Settings className="w-5 h-5 text-primary" />
                  <h3 className={`text-lg font-semibold ${theme.text.primary}`}>
                    Processing Parameters
                  </h3>
                </div>
                
                {/* Project/Organization Name Input */}
                <div className="mb-6">
                  <label className={`block text-sm font-medium ${theme.text.primary} mb-2`}>
                    Project/Organization Name
                  </label>
                  <input
                    type="text"
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    placeholder="e.g., MyCompany, ProjectX, ClientName"
                    className={`w-full px-4 py-3 bg-bg-base border border-border rounded-lg ${theme.text.primary} focus:outline-none focus:ring-2 focus:ring-primary`}
                  />
                  <p className={`text-xs ${theme.text.tertiary} mt-2`}>
                    Job will be named: <span className="font-mono">{projectName || 'PartCategory'}-{new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-')}</span>
                  </p>
                </div>
                
                {/* Chunk Size Input */}
                <div className="mb-6">
                  <label className={`block text-sm font-medium ${theme.text.primary} mb-2`}>
                    Rows per chunk
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={chunkSize}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val !== '') {
                        setChunkSize(Number(val) || chunkSize);
                      }
                      const error = validatePositiveInteger(val, 10, 100, 'Rows per chunk');
                      setChunkSizeError(error);
                    }}
                    onBlur={() => {
                      const error = validatePositiveInteger(chunkSize, 10, 100, 'Rows per chunk');
                      setChunkSizeError(error);
                    }}
                    aria-invalid={!!chunkSizeError}
                    aria-describedby={chunkSizeError ? 'chunk-size-error' : undefined}
                    className={`w-full px-4 py-3 bg-bg-base border ${
                      chunkSizeError ? 'border-qc-error' : 'border-border'
                    } rounded-lg text-2xl font-data ${theme.text.primary} focus:outline-none focus:ring-2 ${
                      chunkSizeError ? 'focus:ring-qc-error' : 'focus:ring-primary'
                    } text-center`}
                  />
                  {chunkSizeError && (
                    <p id="chunk-size-error" className="text-sm text-qc-error mt-1 text-center" role="alert">
                      {chunkSizeError}
                    </p>
                  )}
                  <p className={`text-xs ${theme.text.tertiary} mt-2 text-center`}>
                    Range: 10-100 rows
                  </p>
                </div>

                {/* Max Concurrent Input */}
                <div className="mb-6">
                  <label className={`block text-sm font-medium ${theme.text.primary} mb-2`}>
                    Max concurrent requests
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={maxConcurrent}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val !== '') {
                        setMaxConcurrent(Number(val) || maxConcurrent);
                      }
                      const error = validatePositiveInteger(val, 1, 50, 'Max concurrent requests');
                      setMaxConcurrentError(error);
                    }}
                    onBlur={() => {
                      const error = validatePositiveInteger(maxConcurrent, 1, 50, 'Max concurrent requests');
                      setMaxConcurrentError(error);
                    }}
                    aria-invalid={!!maxConcurrentError}
                    aria-describedby={maxConcurrentError ? 'max-concurrent-error' : undefined}
                    className={`w-full px-4 py-3 bg-bg-base border ${
                      maxConcurrentError ? 'border-qc-error' : 'border-border'
                    } rounded-lg text-2xl font-data ${theme.text.primary} focus:outline-none focus:ring-2 ${
                      maxConcurrentError ? 'focus:ring-qc-error' : 'focus:ring-primary'
                    } text-center`}
                  />
                  {maxConcurrentError && (
                    <p id="max-concurrent-error" className="text-sm text-qc-error mt-1 text-center" role="alert">
                      {maxConcurrentError}
                    </p>
                  )}
                  <p className={`text-xs ${theme.text.tertiary} mt-2 text-center`}>
                    Range: 1-50 requests
                  </p>
                </div>

                {/* Temperature Slider */}
                <div>
                  <label className={`block text-sm font-medium ${theme.text.primary} mb-2`}>
                    Temperature: {temperature.toFixed(1)}
                  </label>
                  <input
                    type="range"
                    value={temperature}
                    onChange={(e) => setTemperature(parseFloat(e.target.value))}
                    min="0.1"
                    max="1.0"
                    step="0.1"
                    className="w-full h-2 bg-bg-base rounded-lg appearance-none cursor-pointer accent-primary"
                  />
                  <div className="flex justify-between text-xs mt-2">
                    <span className={theme.text.tertiary}>0.1 (Precise)</span>
                    <span className={theme.text.tertiary}>0.5 (Balanced)</span>
                    <span className={theme.text.tertiary}>1.0 (Creative)</span>
                  </div>
                </div>

                {/* Output Token Multiplier */}
                <div>
                  <label className={`block text-sm font-medium ${theme.text.primary} mb-2`}>
                    Estimated output token multiplier
                  </label>
                  <div className={`w-full px-4 py-3 bg-bg-base/50 border border-border rounded-lg text-2xl font-data ${theme.text.tertiary} text-center`}>
                    1.8x
                  </div>
                  <p className={`text-xs ${theme.text.tertiary} mt-2 text-center`}>
                    Multiplied by input tokens for cost estimation
                  </p>
                </div>
              </div>
            </div>

            {/* Right Column: Cost Estimation (Sticky) */}
            <div className="lg:sticky lg:top-8 h-fit" data-testid="cost-panel">
              <div className="bg-bg-subtle border-2 border-interactive-primary/20 rounded-2xl p-6">
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="w-5 h-5 text-primary" />
                    <h3 className={`text-lg font-semibold ${theme.text.primary}`}>
                      Cost Estimation
                    </h3>
                  </div>
                  <p className={`text-sm ${theme.text.secondary}`}>
                    Estimated processing cost
                  </p>
                </div>

                <button
                  onClick={handleEstimateCost}
                  disabled={estimating || !selectedFileId}
                  className="w-full py-3 px-4 bg-interactive-primary text-text-inverse rounded-lg font-semibold hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed mb-6"
                >
                  {estimating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Calculating...
                    </>
                  ) : (
                    <>
                      <Calculator className="w-4 h-4" />
                      Calculate Cost
                    </>
                  )}
                </button>

                {estimatedCost && (
                  <div className="space-y-4">
                    <div className="bg-bg-surface rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="w-4 h-4 text-text-tertiary" />
                        <span className={`text-sm ${theme.text.secondary}`}>
                          Input Tokens
                        </span>
                      </div>
                      <p className={`text-2xl font-data ${theme.text.primary}`}>
                        {estimatedCost.estimated_tokens.total_input_tokens.toLocaleString()}
                      </p>
                    </div>

                    <div className="bg-bg-surface rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <FileOutput className="w-4 h-4 text-text-tertiary" />
                        <span className={`text-sm ${theme.text.secondary}`}>
                          Output Tokens
                        </span>
                      </div>
                      <p className={`text-2xl font-data ${theme.text.primary}`}>
                        {estimatedCost.estimated_tokens.total_output_tokens.toLocaleString()}
                      </p>
                    </div>

                    <div className="bg-bg-surface rounded-lg p-4 border-2 border-primary">
                      <div className="flex items-center gap-2 mb-2">
                        <DollarSign className="w-4 h-4 text-primary" />
                        <span className={`text-sm ${theme.text.secondary}`}>
                          Total Cost
                        </span>
                      </div>
                      <p className={`text-3xl font-data ${theme.text.primary}`}>
                        ${estimatedCost.estimated_cost.usd.toFixed(4)}
                      </p>
                      <p className={`text-sm ${theme.text.secondary} mt-1`}>
                        SGD {estimatedCost.estimated_cost.sgd.toFixed(2)}
                      </p>
                    </div>

                    {estimatedCost.processing_time_minutes && (
                      <div className="bg-bg-surface rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Clock className="w-4 h-4 text-text-tertiary" />
                          <span className={`text-sm ${theme.text.secondary}`}>
                            Est. Processing Time
                          </span>
                        </div>
                        <p className={`text-2xl font-data ${theme.text.primary}`}>
                          ~{estimatedCost.processing_time_minutes} min
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Cost Confirmation */}
              {estimatedCost && (
                <div className="mt-6">
                  <CostConfirmation
                    costSGD={estimatedCost.estimated_cost.sgd}
                    onConfirmationChange={setCostConfirmed}
                  />
                </div>
              )}

              {/* Start Processing Button */}
              <button
                onClick={handleStartProcessing}
                disabled={starting || !selectedFileId || !estimatedCost || !costConfirmed || !!chunkSizeError || !!maxConcurrentError}
                className="w-full mt-6 py-4 px-8 text-lg bg-interactive-primary text-text-inverse rounded-lg font-semibold hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="start-button"
              >
                {starting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Starting...
                  </>
                ) : (
                  <>
                    <Play className="w-5 h-5" />
                    Start Processing
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
      </div>
      )} {/* end csv mode */}
    </div>
  );
};

export default PartCategoryWorkflow;
